import React from 'react';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import { AreaChart } from 'components';
import { formatters } from 'services';
import { selectors } from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { LibOption } from './helpers';

interface Props {
  historical: boolean;
  hasSaved: boolean;
  loading: boolean;
  /** ric -> option, used to label component series with friendly names */
  optionIndex: Record<string, LibOption>;
  /** when > 0 the saved benchmark is a constant (flat line); used to pin the
   *  y-axis to ±(constant + 30%) so a small % doesn't auto-scale to ±150%. */
  constantValue?: number;
  /** individual benchmark type — no single shared series to preview. */
  individual?: boolean;
}

const placeholder = (text: string) => (
  <div className="bmv2-preview-empty">
    <LineChartOutlined className="bmv2-empty__icon" />
    <span className="bmv2-empty__sub">{text}</span>
  </div>
);

const escapeHtml = (value: string) =>
  value.replace(/[&<>"]/g, ch =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
/** 'YYYY-MM-DD' -> '12 May 2026' (manual parse to avoid TZ day-shift). */
const formatIsoDate = (iso?: string): string => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

/**
 * Permanent benchmark-return tile. Shows the saved benchmark's return path with
 * the composite "Benchmark" line plus one line per constituent. The tooltip
 * enumerates every series (composite + each constituent) with its return — the
 * same shape as the Benchmark & Players Evolution chart.
 */
const BenchmarkPreviewV2: React.FC<Props> = ({
  historical,
  hasSaved,
  loading,
  optionIndex,
  constantValue,
  individual,
}) => {
  const session = useSelector(selectors.activeSession);
  const preview = session.bechmark_preview_new;
  const composite = preview?.composite ?? [];

  // For a constant benchmark, pin the y-axis to ±(constant × 1.5) — i.e. 50%
  // headroom over the value itself — so the flat line reads clearly instead of
  // auto-scaling to the chart's defaults.
  const constantSpan =
    constantValue != null && constantValue > 0 ? constantValue * 1.5 : null;

  const data = composite.map((y, index) => ({
    x: index * session.frequency + 1,
    y,
  }));

  // ISO date per composite datapoint, formatted for the tooltip header.
  const labelDates = (preview?.dates ?? []).map(formatIsoDate);

  // Component series, labelled with the constituent's friendly name (falling
  // back to the raw RIC/key). Only shown when there's more than one — a single
  // constituent's line is identical to the composite. Each component array is
  // prepended with a 0 (its return at the session start) so it lines up with the
  // composite's leading start-date point and the `dates` axis.
  const components = preview?.components ?? {};
  const additionalLines: { name: string; data: { x: number; y: number }[] }[] =
    [];
  if (Object.keys(components).length > 1) {
    Object.entries(components).forEach(([key, comp]) => {
      const values = [0, ...comp.values];
      additionalLines.push({
        name: optionIndex[key]?.name ?? key,
        data: values.map((y, index) => ({
          x: index * session.frequency + 1,
          y,
        })),
      });
    });
  }

  const xaxis = {
    tooltip: { enabled: false },
    labels: { show: false, formatter: (value: unknown) => `Day ${value}` },
  };

  // Custom tooltip: header + one row per series (dot · name · return%).
  const tooltip = {
    shared: true,
    intersect: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    custom: ({ dataPointIndex, w }: any) => {
      const names: string[] = w.globals.seriesNames || [];
      const colors: string[] = w.globals.colors || [];
      const xVal = w.globals.seriesX?.[0]?.[dataPointIndex];
      const headParts: string[] = [];
      if (Number.isFinite(xVal)) headParts.push(`Day ${xVal}`);
      if (labelDates[dataPointIndex])
        headParts.push(labelDates[dataPointIndex]);
      const head = headParts.length
        ? `<div class="bmv2-tt-head">${escapeHtml(headParts.join(' · '))}</div>`
        : '';
      const rows = names
        .map((name, i) => {
          const val = w.globals.series?.[i]?.[dataPointIndex];
          const color = colors[i] || '#9aa7b8';
          return (
            `<div class="bmv2-tt-row">` +
            `<span class="bmv2-tt-dot" style="background:${color}"></span>` +
            `<span class="bmv2-tt-name">${escapeHtml(String(name))}</span>` +
            `<span class="bmv2-tt-val">${
              val == null ? '—' : formatters.percent1Digit(val)
            }</span>` +
            `</div>`
          );
        })
        .join('');
      return `<div class="bmv2-tt">${head}${rows}</div>`;
    },
  };

  let body: React.ReactNode;
  if (individual) {
    body = placeholder(
      'Individual benchmarks — each team builds and manages their own.',
    );
  } else if (!historical) {
    body = placeholder('Return preview is available for historical sessions.');
  } else if (!hasSaved) {
    body = placeholder(
      'Save a benchmark basket to preview its return path over the session.',
    );
  } else if (composite.length === 0 && (preview?.errors?.length ?? 0) > 0) {
    // Failed preview (e.g. a component became ineligible) — show an error state instead of an
    // indefinite spinner. Once the saved composition is cleared, hasSaved goes false and the empty
    // placeholder above takes over.
    body = placeholder(
      'Could not compute the benchmark return for the current selection.',
    );
  } else if (composite.length === 0) {
    body = (
      <div className="bmv2-preview-empty">
        <Spin />
        <span className="bmv2-empty__sub">Computing benchmark return…</span>
      </div>
    );
  } else {
    body = (
      <div className="bmv2-preview-chart">
        <AreaChart
          title=""
          dataSeriesName="Benchmark"
          data={data}
          yaxisFormatter={formatters.percent1Digit}
          xaxis={xaxis}
          height="100%"
          style={{ height: '100%' }}
          tooltip={tooltip}
          {...(constantSpan != null
            ? { yMin: -constantSpan, yMax: constantSpan }
            : {})}
          {...(additionalLines.length > 0 ? { additionalLines } : {})}
        />
      </div>
    );
  }

  return (
    <SettingsCard
      title="Benchmark return"
      className="bmv2-card bmv2-flexbody"
      extra={
        loading && historical && hasSaved ? (
          <Spin size="small" />
        ) : (
          <span className="bmv2-pill-stat">
            {session.start_date?.split('T')[0]} →{' '}
            {session.end_date?.split('T')[0]}
          </span>
        )
      }
    >
      {body}
    </SettingsCard>
  );
};

export default BenchmarkPreviewV2;
